import type { BulkOrderCreationRow } from "./bulkOrderCreationMock.ts";
import priceSnapshot from "./suggestedOrdersDemoPrices.json" with { type: "json" };
import workbook from "./suggestedOrdersWorkbookDemo.json" with { type: "json" };

export type SuggestedOrdersDemoCell = boolean | number | string | null;
export type SuggestedOrdersDemoRecord = Record<string, SuggestedOrdersDemoCell>;

export interface SuggestedOrdersDemoRow {
  sku: string;
  orders: SuggestedOrdersDemoRecord;
  details: SuggestedOrdersDemoRecord;
}

const SUGGESTED_ORDERS_DEMO_SHIP_DATE = "10/06/26";
const SUGGESTED_ORDERS_DEMO_WAREHOUSE = "W01 - Atlanta";
const demoFirstCosts: Readonly<Record<string, number>> =
  priceSnapshot.firstCostBySku;

const ORDERS_COLUMNS = [
  "Primary",
  "SKU",
  "Description",
  "Theme",
  "Available",
  "ExPr Avail",
  "ExPr AAO",
  "Weekly Sales",
  "New WOS",
  "Sales Ratio",
  "Order",
  "AAO",
  "AAOR",
] as const;

export const SUGGESTED_ORDERS_RESULTS_DEFAULT_COLUMNS = [
  "Primary",
  "SKU",
  "Flow",
  "Description",
  "Order",
  "AAO",
  "AAOR",
] as const;
export const SUGGESTED_ORDERS_RESULTS_COLUMNS: readonly string[] = [
  ...new Set([...SUGGESTED_ORDERS_RESULTS_DEFAULT_COLUMNS, ...ORDERS_COLUMNS]),
];
export const SUGGESTED_ORDERS_COLUMNS_STORAGE_KEY =
  "redisku.suggested-orders.results-columns.v1";

function normalizeCell(value: unknown): SuggestedOrdersDemoCell {
  if (
    value === null ||
    typeof value === "boolean" ||
    typeof value === "number" ||
    typeof value === "string"
  ) {
    return value;
  }
  throw new TypeError(
    "Suggested Orders demo export contains an unsupported cell."
  );
}

function normalizeRecord(record: object): SuggestedOrdersDemoRecord {
  return Object.fromEntries(
    Object.entries(record).map(([column, value]) => [
      column,
      normalizeCell(value),
    ])
  );
}

const exportedOrderRows = workbook.orders.map((row) => normalizeRecord(row));
const exportedDetailRows = workbook.details.map((row) => normalizeRecord(row));

if (
  exportedOrderRows.length === 0 ||
  exportedOrderRows.length !== exportedDetailRows.length
) {
  throw new TypeError(
    "Suggested Orders demo exports have mismatched row counts."
  );
}

export const SUGGESTED_ORDERS_DEMO_ROW_COUNT = exportedOrderRows.length;
export const SUGGESTED_ORDERS_DEMO_ORDERS_COLUMNS = ORDERS_COLUMNS;
export const SUGGESTED_ORDERS_DEMO_DETAILS_COLUMNS = Object.keys(
  exportedDetailRows[0] ?? {}
);

export function calculateSuggestedOrdersAaoRatio(
  aao: number,
  primaryAao: number
): number {
  if (
    !Number.isFinite(aao) ||
    !Number.isFinite(primaryAao) ||
    aao < 0 ||
    primaryAao < 0
  ) {
    throw new TypeError(
      "Suggested Orders AAO values must be nonnegative numbers."
    );
  }
  if (aao === 0 || primaryAao === 0) return 0;
  return aao / primaryAao;
}

function getNumber(record: SuggestedOrdersDemoRecord, column: string): number {
  const value = record[column];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(
      `Suggested Orders demo export has an invalid numeric value in "${column}".`
    );
  }
  return value;
}

function getText(record: SuggestedOrdersDemoRecord, column: string): string {
  const value = record[column];
  if (typeof value !== "string" && typeof value !== "number") {
    throw new TypeError(
      `Suggested Orders demo export has an invalid value in "${column}".`
    );
  }
  return String(value);
}

function randomSample(random: () => number): number {
  const sample = random();
  if (!Number.isFinite(sample) || sample < 0 || sample >= 1) {
    throw new TypeError(
      "Suggested Orders demo random source must return [0, 1)."
    );
  }
  return sample;
}

function randomIntegerBelow(limit: number, random: () => number): number {
  return Math.floor(randomSample(random) * limit);
}

function randomDecimalBelowOneThousand(random: () => number): number {
  return Math.floor(randomSample(random) * 1_000_000) / 1000;
}

export function createSuggestedOrdersDemoRows(
  random: () => number = Math.random,
  startingQuantity?: (index: number) => number
): SuggestedOrdersDemoRow[] {
  const rows = exportedOrderRows.map((exportedOrder, index) => {
    const order = { ...exportedOrder };
    const details = normalizeRecord(exportedDetailRows[index] ?? {});
    const sku = getText(order, "SKU");
    if (sku !== getText(details, "SKU")) {
      throw new TypeError(
        `Suggested Orders demo exports are not aligned at SKU ${sku}.`
      );
    }

    const available = randomIntegerBelow(1000, random);
    const weeklySales = randomDecimalBelowOneThousand(random);
    const newWos = randomDecimalBelowOneThousand(random);
    const orderQuantity = startingQuantity
      ? startingQuantity(index)
      : randomIntegerBelow(1000 - available, random);
    if (
      !Number.isSafeInteger(orderQuantity) ||
      orderQuantity < 0 ||
      orderQuantity > 999 - available
    ) {
      throw new TypeError(
        "Suggested Orders starting quantities must be whole numbers within the available Order range."
      );
    }
    const aao = available + orderQuantity;

    order.Available = available;
    order["Weekly Sales"] = weeklySales;
    order["New WOS"] = newWos;
    order.Order = orderQuantity;
    order.AAO = aao;
    details.Available = available;
    details["Weekly Sales"] = weeklySales;
    details.Order = orderQuantity;
    details["Avail After Order"] = aao;

    return { sku, orders: order, details };
  });
  const primaryRows = rows.filter((row) => row.orders.Primary === true);
  if (primaryRows.length !== 1) {
    throw new TypeError(
      "Suggested Orders demo export must contain exactly one Primary SKU."
    );
  }

  const primaryRow = primaryRows[0];
  if (!primaryRow) {
    throw new TypeError(
      "Suggested Orders demo export must contain exactly one Primary SKU."
    );
  }
  const primaryAao = getNumber(primaryRow.orders, "AAO");
  for (const row of rows) {
    row.orders.AAOR = calculateSuggestedOrdersAaoRatio(
      getNumber(row.orders, "AAO"),
      primaryAao
    );
  }
  return rows;
}

export function buildSuggestedOrdersDemoShippingRows(
  demoRows: SuggestedOrdersDemoRow[]
): BulkOrderCreationRow[] {
  const shippingRows: BulkOrderCreationRow[] = [];
  for (const row of demoRows) {
    const requestedUnits = getNumber(row.orders, "Order");
    if (requestedUnits === 0) continue;
    const unitCube = getNumber(row.details, "Cube");
    const unitPriceUsd = demoFirstCosts[row.sku];
    if (
      unitPriceUsd === undefined ||
      !Number.isFinite(unitPriceUsd) ||
      unitPriceUsd < 0
    ) {
      throw new TypeError(
        `Suggested Orders demo price snapshot has no valid FirstCost for SKU ${row.sku}.`
      );
    }
    if (unitCube < 0) {
      throw new TypeError(
        `Suggested Orders demo export has negative cube for SKU ${row.sku}.`
      );
    }

    shippingRows.push({
      sku: row.sku,
      unitPriceUsd,
      description: getText(row.orders, "Description"),
      shipDate: SUGGESTED_ORDERS_DEMO_SHIP_DATE,
      requestedUnits,
      shipToWarehouse: SUGGESTED_ORDERS_DEMO_WAREHOUSE,
      status: "Ready",
      cubicFeet: Math.round(unitCube * requestedUnits * 10) / 10,
    });
  }
  return shippingRows;
}
